import {
  Button,
  ButtonProps,
  Checkbox,
  makeStyles,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import { WbTooltip } from '../../WbTooltip';
import {
  CSSProperties,
  MouseEvent,
  useMemo,
  Fragment,
  HTMLProps,
  ReactNode,
} from 'react';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import clsx from 'clsx';
import { useWbTableContext } from '../context/WbTableContext';
import { TableCellProps } from '../types';
import { getCommonPinningStyles } from '../utils/columnPinningUtils';
import { useTruncatedStyles } from '../../WbTruncatedTypographyWrapper';
import { DETAIL_EXPANDER_ID, SELECTOR_ID } from '../constants';
import { getColumnId } from '../utils/utils';
import { getCellWidthStyle } from '../utils/columnResizingUtils';
import { Header } from '@tanstack/react-table';

const useStyles = makeStyles(
  theme => ({
    sortableButton: {
      padding: theme.spacing(0.5),
      width: 'calc(100% - 4px)',
      '&:focus': {
        borderRadius: 0,
      },
    },
    tableCell: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      marginRight: -theme.spacing(1),
      overflow: 'hidden',
      userSelect: 'none',
      touchAction: 'none',
      textAlign: 'left',
    },
    sortable: {
      cursor: 'pointer',
    },
    privateCell: {
      padding: theme.spacing(0.5),
      userSelect: 'none',
      touchAction: 'none',
      overflow: 'hidden',
      textOverflow: 'clip',
    },
    headerCell: {
      'th&': {
        // undo MUI default's "uppercase" to not impact header description
        textTransform: 'none',
        verticalAlign: 'top',
      },
    },
    headerTitleContent: {
      minHeight: 26,
      display: 'flex',
      textTransform: 'uppercase',
      alignItems: 'center',
      width: '100%',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
    },
  }),
  { name: 'WbTableHeader' },
);

const useHeaderInteractionStyles = makeStyles(
  () => ({
    root: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      // leave space to resizer
      borderRadius: 0,
      right: 4,
    },
  }),
  {
    name: 'WbHeaderInteraction',
  },
);

const useResizerStyle = makeStyles(
  theme => ({
    columnResizer: {
      width: '15px',
      height: '100%',
      position: 'absolute',
      right: 0,
      top: 0,
      cursor: 'ew-resize',
      userSelect: 'none',
      touchAction: 'none',
      '&:hover $handler': {
        transform: 'translateY(-50%) scaleX(1.5) scaleY(1)',
        background: theme.palette.primary.main,
        width: '2px',
      },
    },
    handler: {
      width: '1px',
      height: 'calc(100% - 16px)',
      background: theme.palette.grey[300],
      position: 'absolute',
      right: '20%',
      top: '50%',
      transform: 'translateY(-50%)',
      transition: 'transform 0.2s ease-in-out',
    },
    handlerPinnedLeft: {
      right: -1,
    },
    mask: {
      width: '10px',
      height: '100%',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 0,
    },
  }),
  {
    name: 'WbHeaderResizer',
  },
);

export interface TableHeaderProps<T> {
  style?: CSSProperties;
  multiSelection?: boolean;
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  className?: string;
}

type ColumnResizerProps = HTMLProps<HTMLDivElement> & { pinnedLeft?: boolean };

const ColumnResizer = ({ pinnedLeft, ...props }: ColumnResizerProps) => {
  const classes = useResizerStyle();
  return (
    <div {...props} className={clsx(classes.columnResizer)}>
      <div className={classes.mask} />
      <div
        className={clsx(
          classes.handler,
          pinnedLeft && classes.handlerPinnedLeft,
        )}
      />
    </div>
  );
};

function AllSelector<T>({ selectableRows }: { selectableRows: T[] }) {
  const { selectedItems, setSelectedItems, components, selectionDisabled } =
    useWbTableContext<T>();

  const loading = Boolean(components.tableLoader?.loading);

  const disabled =
    selectableRows.length === 0 ||
    (selectedItems && selectableRows.length < selectedItems?.length);

  // show no selector while loading
  if (loading) return null;

  return (
    <Checkbox
      style={{ padding: '4px' }}
      disabled={disabled || selectionDisabled}
      indeterminate={
        (selectedItems &&
          selectedItems?.length > 0 &&
          selectedItems?.length !== selectableRows.length) ||
        selectableRows.length === 0
      }
      onClick={() => {
        if (selectedItems!.length === selectableRows.length) {
          setSelectedItems!([]);
        } else {
          setSelectedItems!(selectableRows);
        }
      }}
      checked={selectedItems?.length === selectableRows.length}
      inputProps={{ 'aria-label': 'select all' }}
    />
  );
}

const SortingIcon = ({
  enable,
  sortDirection,
}: {
  enable: boolean;
  sortDirection: boolean | string | null;
}): React.ReactElement => {
  if (!enable) return <></>;

  if (sortDirection === 'asc') return <ArrowUpwardIcon fontSize="small" />;

  if (sortDirection === 'desc') return <ArrowDownwardIcon fontSize="small" />;

  return <ImportExportIcon fontSize="small" />;
};

const HeaderInteraction = <T,>({
  cell,
  onClick,
  header,
}: { cell: TableCellProps<T>; header: Header<T, unknown> } & ButtonProps) => {
  const classes = useHeaderInteractionStyles();

  const nextSortingOrder = header.column.getNextSortingOrder();

  const ariaLabel = nextSortingOrder
    ? `Sort by ${cell.headerName ?? 'this column'} ${nextSortingOrder}`
    : `Clear sorting by ${cell.headerName ?? 'this column'} `;

  return (
    <Button aria-label={ariaLabel} className={classes.root} onClick={onClick} />
  );
};

export function TableHeader<T>({ style, className }: TableHeaderProps<T>) {
  const {
    rows,
    checkIfDisabled,
    tanstackTableInstance,
    columns,
    selection,
    tooltipIfDisabled,
  } = useWbTableContext<T>();

  const theme = useTheme();
  const classes = useStyles();
  const truncatedClass = useTruncatedStyles({});

  const selectableRows = useMemo(() => {
    return rows.filter(r => !checkIfDisabled?.(r));
  }, [rows, checkIfDisabled]);

  const getHeaderContent = (
    cell: TableCellProps<T>,
    {
      customRender,
      sortingIcon,
    }: { customRender?: ReactNode; sortingIcon?: ReactNode },
  ) => {
    let component = (
      <div className={classes.headerTitleContent}>
        {customRender ? (
          customRender
        ) : (
          <div style={{ overflow: 'hidden' }} color="primary">
            <Typography
              className={truncatedClass.truncated}
              style={{ fontSize: theme.typography.body2.fontSize }}
            >
              {cell.headerName}
            </Typography>
            {cell.headerInfoTooltip && (
              <WbTooltip title={cell.headerInfoTooltip}>
                <InfoOutlinedIcon fontSize="small" />
              </WbTooltip>
            )}
          </div>
        )}
        {sortingIcon}
      </div>
    );

    if (cell.headerTooltip)
      component = <WbTooltip title={cell.headerTooltip} children={component} />;

    if (cell.headerDescription)
      component = (
        <>
          {component}
          {cell.headerDescription}
        </>
      );

    return component;
  };

  return (
    <TableHead style={{ ...style }} className={className}>
      <TableRow>
        {columns.map((cell, cellIndex) => {
          const colId = getColumnId(cell);
          const isSelector = colId === SELECTOR_ID;
          const isDetailExpander = colId === DETAIL_EXPANDER_ID;

          if (isDetailExpander)
            return (
              <TableCell
                style={{ ...getCellWidthStyle(colId) }}
                data-id={colId}
                className={clsx(classes.privateCell, classes.headerCell)}
              />
            );

          if (isSelector && selection === 'multiple')
            return (
              <TableCell
                style={{ ...getCellWidthStyle(colId) }}
                data-id={colId}
                className={clsx(classes.privateCell, classes.headerCell)}
              >
                {getHeaderContent(cell, {
                  customRender:
                    tooltipIfDisabled && selectableRows.length === 0 ? (
                      <WbTooltip title={tooltipIfDisabled}>
                        <span>
                          <AllSelector selectableRows={selectableRows} />
                        </span>
                      </WbTooltip>
                    ) : (
                      <AllSelector selectableRows={selectableRows} />
                    ),
                })}
              </TableCell>
            );

          if (isSelector && selection === 'single')
            return (
              <TableCell
                style={{ ...getCellWidthStyle(colId) }}
                data-id={colId}
                className={clsx(classes.privateCell, classes.headerCell)}
              />
            );

          const column = tanstackTableInstance.getColumn(colId);

          const header = tanstackTableInstance
            .getFlatHeaders()
            .find((h: { id: string }) => h.id === column?.id);

          if (!column || !header) throw new Error('Column misconfiguration');

          const isPinned = column.getIsPinned();

          return (
            <Fragment key={`header-cell-index-${cellIndex}`}>
              <TableCell
                data-id={colId}
                className={clsx(
                  classes.tableCell,
                  classes.headerCell,
                  column.columnDef.enableSorting && classes.sortable,
                )}
                style={{
                  ...getCellWidthStyle(colId),
                  ...(isPinned
                    ? {
                        ...getCommonPinningStyles(column, theme, true),
                        ...(isPinned === 'left' ? { overflow: 'visible' } : {}),
                      }
                    : {}),
                }}
              >
                {column.columnDef.enableSorting && (
                  <HeaderInteraction
                    cell={cell}
                    header={header}
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();

                      if (column.columnDef.enableSorting) {
                        const handler = column.getToggleSortingHandler();
                        handler?.(e);
                      }
                    }}
                  />
                )}
                {getHeaderContent(cell, {
                  sortingIcon: column.columnDef.enableSorting ? (
                    <SortingIcon
                      enable={column.columnDef.enableSorting}
                      sortDirection={header.column.getIsSorted()}
                    />
                  ) : undefined,
                })}
                {column.columnDef.enableResizing && (
                  <ColumnResizer
                    {...{
                      onClick: (e: MouseEvent) => {
                        e.stopPropagation();
                      },
                      onMouseDown: e => {
                        header.getResizeHandler()(e);
                      },
                      onTouchStart: e => {
                        header.getResizeHandler()(e);
                      },
                      pinnedLeft: isPinned === 'left',
                    }}
                  />
                )}
              </TableCell>
            </Fragment>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
